import { PaperClipOutlined, TagOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Avatar, Button, Flex, Input, Popconfirm, Tag, Typography, Upload } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import type { EmojiEnum } from "../../app/generated-axios-client/myModel/emoji-enum";
import { palette } from "../../app/theme";
import { formatRelativeTime } from "../utils/formatRelativeTime";
import { CommentAttachments, type CommentFileRelation } from "./CommentAttachments";
import styles from "./CommentItem.module.css";
import { CommentReactionPicker } from "./CommentReactionPicker";
import { type CommentReaction, CommentReactions } from "./CommentReactions";
import type { PreviewableFile } from "./FilePreviewModal";

export interface CommentAuthor {
  id: number;
  first_name?: string;
  last_name?: string;
  username: string;
}

export interface CommentEntry {
  id?: string;
  content?: string | null;
  user: CommentAuthor;
  reply_to?: CommentAuthor;
  created_at?: string;
  ticket?: string | null;
  ticket_item?: string | null;
  file_relations?: CommentFileRelation[];
  reactions?: CommentReaction[];
}

export type TaskSummary = { number: number; componentLabel?: string };

export const AVATAR_PALETTE = [
  palette.dashBlue,
  palette.dashGreen,
  palette.dashOrange,
  palette.dashPurple,
  palette.dashCyan,
  palette.dashRed,
];

export function getInitials(user: CommentAuthor): string {
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return user.username.slice(0, 2).toUpperCase();
}

export function getDisplayName(user: CommentAuthor): string {
  const parts = [user.first_name?.trim(), user.last_name?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : user.username;
}

type CommentItemProps = {
  comment: CommentEntry;
  currentUserId: number | undefined;
  onReply: (user: CommentAuthor, ticketItem?: string | null) => void;
  onEdit: (id: string, content: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onAttachFile: (file: File, comment: CommentEntry) => Promise<void>;
  onToggleReaction: (comment: CommentEntry, emoji: EmojiEnum) => void;
  pendingReactionEmojis: EmojiEnum[];
  getDownloadUrl: (file: PreviewableFile) => string;
  intl: IntlShape;
  taskMap?: Record<string, TaskSummary>;
  messengerLayout?: boolean;
};

export const CommentItem = ({
  comment,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
  onAttachFile,
  onToggleReaction,
  pendingReactionEmojis,
  getDownloadUrl,
  intl,
  taskMap,
  messengerLayout = false,
}: CommentItemProps) => {
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState(comment.content ?? "");
  const [saving, setSaving] = useState(false);
  const [pendingAttachmentCount, setPendingAttachmentCount] = useState(0);
  const [attachmentFailed, setAttachmentFailed] = useState(false);

  const handleAttachFile = async (file: File) => {
    setAttachmentFailed(false);
    setPendingAttachmentCount((count) => count + 1);
    try {
      await onAttachFile(file, comment);
    } catch {
      setAttachmentFailed(true);
    } finally {
      setPendingAttachmentCount((count) => count - 1);
    }
  };

  const isOwner = comment.user.id === currentUserId;

  const toggleReaction = (emoji: EmojiEnum) => onToggleReaction(comment, emoji);
  const emojisReactedByCurrentUser = (comment.reactions ?? [])
    .filter((reaction) => reaction.user.id === currentUserId)
    .map((reaction) => reaction.emoji);

  const reactionRow = (
    <CommentReactions
      reactions={comment.reactions}
      currentUserId={currentUserId}
      pendingEmojis={pendingReactionEmojis}
      onToggle={toggleReaction}
      getUserName={getDisplayName}
      intl={intl}
      alignEnd={messengerLayout && isOwner}
    />
  );

  const handleSave = async () => {
    if (!comment.id || !editContent.trim()) return;
    setSaving(true);
    try {
      await onEdit(comment.id, editContent.trim());
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content ?? "");
    setEditMode(false);
  };

  const taskSummary = comment.ticket_item ? taskMap?.[comment.ticket_item] : undefined;
  const taskChip = taskSummary && (
    <Tag icon={<TagOutlined />} style={{ width: "fit-content" }}>
      Task #{taskSummary.number}
      {taskSummary.componentLabel ? ` — ${taskSummary.componentLabel}` : ""}
    </Tag>
  );

  const editModeContent = (
    <Flex vertical gap={6}>
      <Input.TextArea
        rows={3}
        value={editContent}
        onChange={(e) => setEditContent(e.target.value)}
        autoFocus
      />
      <Flex gap={8}>
        <Button
          type="primary"
          size="small"
          loading={saving}
          disabled={!editContent.trim()}
          onClick={handleSave}
        >
          Save
        </Button>
        <Button size="small" onClick={handleCancelEdit}>
          Cancel
        </Button>
        <Upload
          multiple
          showUploadList={false}
          customRequest={({ file }) => {
            if (file instanceof File) handleAttachFile(file);
          }}
        >
          <Button
            size="small"
            icon={<PaperClipOutlined />}
            loading={pendingAttachmentCount > 0}
          >
            {intl.formatMessage({ id: "comment_attach_files" })}
          </Button>
        </Upload>
      </Flex>
      {attachmentFailed && (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>
          {intl.formatMessage({ id: "upload_failed" })}
        </Typography.Text>
      )}
    </Flex>
  );

  const attachments = (
    <CommentAttachments
      fileRelations={comment.file_relations}
      getDownloadUrl={getDownloadUrl}
      intl={intl}
    />
  );

  const actionLinks = !editMode && (
    <Flex gap={12}>
      <Typography.Link
        className={styles.actionLink}
        onClick={() => onReply(comment.user, comment.ticket_item)}
      >
        Reply
      </Typography.Link>
      <CommentReactionPicker
        reactedEmojis={emojisReactedByCurrentUser}
        pendingEmojis={pendingReactionEmojis}
        onToggle={toggleReaction}
        intl={intl}
        triggerClassName={styles.actionLink}
      />
      {isOwner && (
        <>
          <Typography.Link
            className={styles.actionLink}
            onClick={() => {
              setEditContent(comment.content ?? "");
              setEditMode(true);
            }}
          >
            Edit
          </Typography.Link>
          <Popconfirm
            title="Delete this comment?"
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
            onConfirm={() => comment.id && onDelete(comment.id)}
          >
            <Typography.Link className={styles.actionLink} type="danger">
              Delete
            </Typography.Link>
          </Popconfirm>
        </>
      )}
    </Flex>
  );

  if (messengerLayout) {
    return (
      <div
        className={
          isOwner ? `${styles.messageRow} ${styles.messageRowOwner}` : styles.messageRow
        }
      >
        <Avatar
          size={32}
          style={{
            backgroundColor: AVATAR_PALETTE[comment.user.id % AVATAR_PALETTE.length],
            flexShrink: 0,
          }}
        >
          {getInitials(comment.user)}
        </Avatar>
        <div
          className={
            isOwner
              ? `${styles.messageColumn} ${styles.messageColumnOwner}`
              : styles.messageColumn
          }
        >
          <Typography.Text type="secondary" className={styles.bubbleMeta}>
            {getDisplayName(comment.user)}
            {comment.created_at && ` · ${formatRelativeTime(comment.created_at)}`}
          </Typography.Text>
          <div
            className={isOwner ? `${styles.bubble} ${styles.bubbleOwner}` : styles.bubble}
          >
            {taskChip}
            {comment.reply_to && (
              <Typography.Text
                style={{ fontSize: 12, fontStyle: "italic", display: "block" }}
              >
                ↳ Replying to {getDisplayName(comment.reply_to)}
              </Typography.Text>
            )}
            {editMode ? (
              editModeContent
            ) : (
              <Typography.Paragraph style={{ marginBottom: 0, fontSize: 13 }}>
                {comment.content}
              </Typography.Paragraph>
            )}
            {attachments}
          </div>
          {reactionRow}
          {actionLinks && <div className={styles.messageActions}>{actionLinks}</div>}
        </div>
      </div>
    );
  }

  return (
    <Flex gap={10} align="flex-start">
      <Avatar
        size="small"
        style={{
          backgroundColor: AVATAR_PALETTE[comment.user.id % AVATAR_PALETTE.length],
          flexShrink: 0,
        }}
      >
        {getInitials(comment.user)}
      </Avatar>
      <Flex vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Text strong style={{ fontSize: 13 }}>
            {getDisplayName(comment.user)}
          </Typography.Text>
          {comment.created_at && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {dayjs(comment.created_at).format("DD/MM/YYYY HH:mm")}
            </Typography.Text>
          )}
        </Flex>
        {comment.reply_to && (
          <Typography.Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
            ↳ Replying to {getDisplayName(comment.reply_to)}
          </Typography.Text>
        )}
        {taskChip}
        {editMode ? (
          editModeContent
        ) : (
          <Typography.Paragraph style={{ marginBottom: 0, fontSize: 13 }}>
            {comment.content}
          </Typography.Paragraph>
        )}
        {attachments}
        {reactionRow}
        {actionLinks}
      </Flex>
    </Flex>
  );
};
